/* ============================================================
   MEMORIES OF MOTION — SHARED PRODUCT GALLERY

   Used by the book and mug product pages. Deliberately tiny, because
   these are SINGLE-VARIANT products: there is no variant to resolve, no
   price to recalculate and no options to filter. All this does is swap
   which image the well shows.

   It does NOT use Dawn's <media-gallery> or <product-info>. Not out of
   preference — Dawn's `updateOptionValues` replaces the whole
   <variant-selects> element on every change via HTMLUpdateUtility, which
   is what cascaded into 67 section renders per click on the photograph
   page. Nothing here can do that: one element, never replaced, one
   listener per thumbnail.

   Add-to-cart is still Shopify's own <product-form>, which this does not
   touch.
   ============================================================ */

class MomProductGallery extends HTMLElement {
  connectedCallback() {
    this.main = this.querySelector('[data-mpp-image]');
    this.thumbs = Array.from(this.querySelectorAll('[data-mpp-thumb]'));
    this.counter = this.querySelector('[data-mpp-counter]');
    // Book page only: the caption under the well and the "Illustration" tag
    // follow the selected image. Absent on the mug page, so nothing changes there.
    this.label = this.querySelector('[data-mpp-label]');
    this.tag = this.querySelector('[data-mpp-tag]');

    // One image is not a gallery — leave the well alone and render nothing.
    if (!this.main || this.thumbs.length < 2) {
      if (this.counter) this.counter.textContent = '';
      return;
    }

    this.thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => this.select(i));
      thumb.addEventListener('keydown', (e) => this.onKey(e, i));
    });

    this.select(0);
  }

  select(index) {
    const thumb = this.thumbs[index];
    if (!thumb) return;

    const img = thumb.querySelector('img');
    if (img) {
      // The thumbnail carries the full-size source, so switching costs no
      // request beyond the image itself and never re-renders the section.
      this.main.src = thumb.dataset.full || img.currentSrc || img.src;
      this.main.alt = thumb.dataset.alt || img.alt || '';
    }

    this.thumbs.forEach((t, i) => {
      t.setAttribute('aria-pressed', i === index ? 'true' : 'false');
      t.tabIndex = i === index ? 0 : -1;
    });

    if (this.counter) {
      this.counter.textContent = `${index + 1} of ${this.thumbs.length}`;
    }
    if (this.label && thumb.dataset.label) this.label.textContent = thumb.dataset.label;
    if (this.tag) this.tag.hidden = !thumb.hasAttribute('data-illustration');
    this.index = index;
  }

  /** Arrow keys move between thumbnails, as a grouped control should. */
  onKey(event, index) {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };
    let next = null;

    if (event.key in step) next = (index + step[event.key] + this.thumbs.length) % this.thumbs.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = this.thumbs.length - 1;
    if (next === null) return;

    event.preventDefault();
    this.select(next);
    this.thumbs[next].focus();
  }
}

if (!customElements.get('mom-product-gallery')) {
  customElements.define('mom-product-gallery', MomProductGallery);
}
